import * as React from "react";
import { useTenant } from "@app/providers/tenant";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { listStores, type StoreSummary } from "@features/billing/api/billing";
import { getStorefrontRootUrl } from "@shared/lib/storefront";
import { useRouterState } from "@tanstack/react-router";
import {
  Check,
  ChevronDown,
  ExternalLink,
  Loader2,
  QrCode,
  Store,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@app/providers/auth";
import { StorefrontQrCodeDialog } from "@shared/ui/StorefrontQrCodeDialog";

const TENANT_LIST_QUERY_KEY = ["tenants.list"] as const;

type TenantStore = StoreSummary & {
  switchId: string;
};

function normalizeStore(store: StoreSummary): TenantStore {
  return {
    ...store,
    switchId: store.tenantId || store.id,
    name: store.name || store.slug || "Unnamed Store",
    slug: store.slug || "",
  };
}

function getStoreUrl(store?: Pick<TenantStore, "slug" | "domain"> | null) {
  if (!store) return "";
  return getStorefrontRootUrl({ slug: store.slug, domain: store.domain });
}

function isCurrentStore(store: TenantStore, tenantId: string | null) {
  return Boolean(tenantId && store.switchId === tenantId);
}

export function TenantSwitcher() {
  const { t } = useTranslation();
  const { getToken, refreshToken } = useAuth();
  const { tenantId, setTenantId } = useTenant();
  const queryClient = useQueryClient();
  const { location } = useRouterState({
    select: (s) => ({ location: s.location }),
  });
  const [open, setOpen] = React.useState(false);
  const [qrOpen, setQrOpen] = React.useState(false);
  const [switchingTenantId, setSwitchingTenantId] = React.useState<
    string | null
  >(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const wasOpenRef = React.useRef(false);
  const hasRecoverableSession = Boolean(getToken() || refreshToken);

  const {
    data: stores = [],
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: TENANT_LIST_QUERY_KEY,
    queryFn: async () => (await listStores()).map(normalizeStore),
    enabled: hasRecoverableSession,
    retry: 1,
    staleTime: 60_000,
  });

  const current = React.useMemo(() => {
    const matched = stores.find((store) => isCurrentStore(store, tenantId));
    if (matched) return matched;
    if (!tenantId) return null;

    return {
      id: tenantId,
      tenantId,
      switchId: tenantId,
      name: stores.length > 0 ? "Unknown Store" : "Loading...",
      slug: "",
    } satisfies TenantStore;
  }, [stores, tenantId]);

  const additionalStoreCount = React.useMemo(
    () => stores.filter((store) => !isCurrentStore(store, tenantId)).length,
    [stores, tenantId],
  );
  const currentStorefrontUrl = getStoreUrl(current);
  const displayName =
    current?.name ||
    t("tenantSwitcher.noStores", { defaultValue: "No stores found" });

  const closeMenu = React.useCallback(() => setOpen(false), []);

  React.useEffect(() => {
    if (!open) return;

    function onDocPointerDown(event: MouseEvent | PointerEvent) {
      const root = containerRef.current;
      const target = event.target as Node | null;
      if (root && target && !root.contains(target)) closeMenu();
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") closeMenu();
    }

    document.addEventListener("pointerdown", onDocPointerDown as any, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener(
        "pointerdown",
        onDocPointerDown as any,
        true,
      );
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [closeMenu, open]);

  React.useEffect(() => {
    closeMenu();
  }, [closeMenu, location.pathname]);

  React.useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      const timer = window.setTimeout(() => {
        const panel = containerRef.current?.querySelector<HTMLElement>(
          '[data-autofocus], button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        );
        panel?.focus();
      }, 0);
      return () => window.clearTimeout(timer);
    }

    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      triggerRef.current?.focus?.();
    }
    return undefined;
  }, [open]);

  async function switchTenant(store: TenantStore) {
    const nextTenantId = store.switchId;
    if (!nextTenantId || nextTenantId === tenantId || switchingTenantId) {
      closeMenu();
      return;
    }

    setSwitchingTenantId(nextTenantId);

    try {
      await queryClient.cancelQueries();
      queryClient.clear();
      queryClient.setQueryData(TENANT_LIST_QUERY_KEY, stores);
      setTenantId(nextTenantId);
      closeMenu();
    } finally {
      setSwitchingTenantId(null);
    }
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("tenantSwitcher.currentStoreLabel", {
          defaultValue: "Tenant: {{store}}",
          store: displayName,
        })}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-primary/5 p-0 text-left hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 md:h-auto md:w-auto md:max-w-[13rem] md:justify-start md:gap-2 md:px-2 md:py-1"
      >
        {isFetching && !open ? (
          <Loader2
            size={16}
            className="animate-spin text-muted-foreground md:hidden"
          />
        ) : (
          <Store className="h-4 w-4 md:hidden" aria-hidden="true" />
        )}
        <span className="hidden text-xs text-muted-foreground lg:inline">
          {t("tenantSwitcher.label", { defaultValue: "Tenant:" })}
        </span>
        <span className="hidden min-w-0 truncate text-sm font-medium md:inline">
          {displayName}
        </span>
        {isFetching && !open ? (
          <Loader2
            size={14}
            className="hidden shrink-0 animate-spin text-muted-foreground md:block"
          />
        ) : (
          <ChevronDown
            size={14}
            className="hidden shrink-0 text-muted-foreground md:block"
          />
        )}
      </button>

      {open ? (
        <div
          role="menu"
          aria-label={t("tenantSwitcher.menuLabel", {
            defaultValue: "Store switcher",
          })}
          className="fixed inset-x-3 top-16 z-50 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg md:absolute md:inset-x-auto md:right-0 md:top-auto md:mt-2 md:w-80"
        >
          <div className="border-b bg-muted/40 px-3 py-2">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {t("tenantSwitcher.availableStores", {
                defaultValue: "Available stores",
              })}
            </div>
            <div className="mt-1 truncate text-sm font-medium text-foreground">
              {displayName}
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto p-1">
            {isLoading ? (
              <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("tenantSwitcher.loading", {
                  defaultValue: "Loading stores...",
                })}
              </div>
            ) : isError ? (
              <div className="space-y-2 px-3 py-3 text-sm">
                <p className="text-destructive">
                  {t("tenantSwitcher.loadFailed", {
                    defaultValue: "Failed to load tenants",
                  })}
                </p>
                <button
                  type="button"
                  className="rounded-md border px-2 py-1 text-xs font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                  onClick={() => void refetch()}
                >
                  {t("tenantSwitcher.retry", { defaultValue: "Retry" })}
                </button>
              </div>
            ) : stores.length === 0 ? (
              <div className="px-3 py-3 text-sm text-muted-foreground">
                {t("tenantSwitcher.noStores", {
                  defaultValue: "No stores found",
                })}
              </div>
            ) : (
              stores.map((store) => {
                const currentStore = isCurrentStore(store, tenantId);
                const storefrontUrl = getStoreUrl(store);
                const isSwitching = switchingTenantId === store.switchId;

                return (
                  <button
                    key={store.switchId}
                    type="button"
                    role="menuitem"
                    aria-current={currentStore ? "true" : undefined}
                    disabled={currentStore || Boolean(switchingTenantId)}
                    data-autofocus={currentStore ? true : undefined}
                    className="flex w-full items-start gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:cursor-default disabled:opacity-100"
                    onClick={() => void switchTenant(store)}
                  >
                    <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                      {isSwitching ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Store className="h-4 w-4" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-medium text-foreground">
                          {store.name}
                        </span>
                        {currentStore ? (
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                            <Check className="h-3 w-3" />
                            {t("tenantSwitcher.current", {
                              defaultValue: "Current",
                            })}
                          </span>
                        ) : null}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                        {storefrontUrl ||
                          (store.slug
                            ? t("tenantSwitcher.storefrontFromSlug", {
                                defaultValue: "Storefront uses slug {{slug}}.",
                                slug: store.slug,
                              })
                            : t("tenantSwitcher.storefrontMissing", {
                                defaultValue:
                                  "Add a store slug or domain to open the storefront.",
                              }))}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {!isLoading &&
          !isError &&
          stores.length > 0 &&
          additionalStoreCount === 0 ? (
            <div className="border-t px-3 py-2 text-xs text-muted-foreground">
              {t("tenantSwitcher.noAdditionalStores", {
                defaultValue: "No other stores are available for this account.",
              })}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-2 border-t bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            {currentStorefrontUrl ? (
              <a
                href={currentStorefrontUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-full items-center gap-1 font-medium text-primary hover:underline"
              >
                <span className="truncate">
                  {t("tenantSwitcher.openCurrentStorefront", {
                    defaultValue: "Open current storefront",
                  })}
                </span>
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              </a>
            ) : (
              <span>
                {t("tenantSwitcher.storefrontMissing", {
                  defaultValue:
                    "Add a store slug or domain to open the storefront.",
                })}
              </span>
            )}
            <button
              type="button"
              disabled={!currentStorefrontUrl}
              className="inline-flex items-center gap-1 font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
              onClick={() => {
                closeMenu();
                setQrOpen(true);
              }}
            >
              <QrCode className="h-3.5 w-3.5 shrink-0" />
              {t("tenantSwitcher.showStorefrontQrCode", {
                defaultValue: "Show storefront QR code",
              })}
            </button>
          </div>
        </div>
      ) : null}

      <StorefrontQrCodeDialog
        open={qrOpen}
        onOpenChange={setQrOpen}
        url={currentStorefrontUrl}
        storeName={current?.name}
      />
    </div>
  );
}
